import { activityTreeApiClient } from "./client";

/** Raw tokenized response from GET /rate-engine/library/{region}/templates */
interface ItemLibraryItemRaw {
  c: string; // resourceCode
  n: string; // resourceDescription
  r: number; // nettRate
}

/**
 * Item Library Item Structure
 */
export interface ItemLibraryItem {
  region: string;
  libraryType: string;
  resourceCode: string;
  resourceDescription: string;
  unit: string;
  baseRate: number;
  nettRate: number;
  resourceGroup: string;
  resourceType: string;
  costCodeCode: string;
  costCodeDescription: string;
}

/** Item library response is a flat array of items. */
export type ItemLibraryResponse = ItemLibraryItem[];

export interface CreateSiteRequest {
  siteName: string;
  /** Opportunity number(s) to tag the site with; a 409 now only fires when both siteName and oppNo match. */
  oppNo?: string;
  createdBy?: string;
}

export interface CreateSiteResponse {
  projectId: number;
  sourceProjectId: number;
  name: string;
  siteName: string;
  oppNo?: string | null;
  regionCode: string;
  createdAt: string | null;
}

/**
 * Item Library API - handles item library related API calls
 * Endpoint: rate-engine/library/{region}/templates
 */
export const itemLibraryApi = {
  /**
   * Fetch item library by region (and optional site name for site-specific rates).
   * @param region - The region code (e.g., "north-west", "southern", etc.)
   * @param siteName - Optional site address for site-specific library lookup
   * @param oppNo - Optional opportunity number(s) disambiguating siteName (exact match)
   * @returns Promise with flat array of item library items
   */
  fetchByRegion: async (region: string, siteName?: string, oppNo?: string): Promise<ItemLibraryResponse> => {
    try {
      const response = await activityTreeApiClient.get<ItemLibraryItemRaw[]>(
        `/rate-engine/library/${region}/templates`,
        siteName ? { params: { siteName, ...(oppNo ? { oppNo } : {}) } } : undefined,
      );
      return response.data.map((raw) => ({
        region,
        libraryType: '',
        resourceCode: raw.c,
        resourceDescription: raw.n,
        unit: '',
        baseRate: raw.r,
        nettRate: raw.r,
        resourceGroup: '',
        resourceType: '',
        costCodeCode: '',
        costCodeDescription: '',
      }));
    } catch (error) {
      console.error(`Error fetching item library for region ${region}:`, error);
      throw error;
    }
  },

  /**
   * Create (or retrieve existing) site library under a regional library.
   * 201 = new site created; 409 = already existed — both are usable.
   * Call is non-blocking from the FE perspective; log failures but don't surface to user.
   */
  createSite: async (
    region: string,
    body: CreateSiteRequest,
  ): Promise<CreateSiteResponse> => {
    const response = await activityTreeApiClient.post<CreateSiteResponse>(
      `/rate-engine/library/${region}/sites`,
      body,
      { validateStatus: (s) => s === 201 || s === 409 },
    );
    return response.data;
  },
};
